/**
 * Feature gating definitions for the Egowall product tiers:
 *   Keepsake  → free
 *   Gallery   → pro   ($9 / month)
 *   Studio    → enterprise ($24 / month)
 *
 * Add a key here whenever a new product surface needs to be gated.
 * The gate is read via `useFeatureGate()` from SubscriptionContext.
 */

export const FEATURE_KEYS = {
  /** Gallery+ — unlimited albums. Free tier shows 3-album soft limit. */
  UNLIMITED_ALBUMS: 'unlimited_albums',
  /** Gallery+ — full 3D walkable gallery wizard + studio. */
  GALLERY_3D: 'gallery_3d',
  /** Gallery+ — link more than one album into a Studio Assets palette. */
  MULTI_SOURCE_ALBUMS: 'multi_source_albums',
  /** Gallery+ — custom wall/floor color picker in Studio Room inspector. */
  STUDIO_CUSTOM_FINISHES: 'studio_custom_finishes',
  /** Gallery+ — allow guests to upload photos to a shared album. */
  GUEST_UPLOADS: 'guest_uploads',
  /** Gallery+ — voice note recordings on album photos. */
  VOICE_NOTES: 'voice_notes',
  /** Gallery+ — anniversary re-opening flows. */
  ANNIVERSARY_REOPENING: 'anniversary_reopening',
  /** Studio — multiple rooms in one gallery (MAIN ROOM ▾ chip). Soft-gated; rooms UI not shipped yet. */
  STUDIO_MULTI_ROOM: 'studio_multi_room',
  /** Gallery+ — Loft and Pavilion procedural room shapes, beyond the free Corridor. */
  STUDIO_PREMIUM_SCENES: 'studio_premium_scenes',
  /** Studio — client workspaces and branding. */
  CLIENT_WORKSPACES: 'client_workspaces',
  /** Studio — RAW photo proofing. */
  RAW_PROOFING: 'raw_proofing',
  /** Studio — album analytics dashboard. */
  ANALYTICS: 'analytics',
  /** Every tier has some prints-per-room cap — this key is only used for the upgrade-modal copy when the cap is hit. See MAX_PRINTS_PER_ROOM. */
  STUDIO_PRINT_QUOTA: 'studio_print_quota',
  /** Gallery+ — raise the upload resize ceiling from 1000px to 4000px. Settable at album creation and in the Share sheet. */
  FULL_RESOLUTION_UPLOAD: 'full_resolution_upload',
} as const

export type FeatureKey = (typeof FEATURE_KEYS)[keyof typeof FEATURE_KEYS]

export interface FeatureDef {
  /** Human-readable label for the paywall modal. */
  label: string
  /** One-line pitch shown in the upgrade prompt. */
  pitch: string
  /** Minimum tier required. */
  requiredTier: 'pro' | 'enterprise'
  /** The pricing page plan name shown in the CTA. */
  upgradeTo: 'Gallery' | 'Studio'
}

export const FEATURES_MAP: Record<FeatureKey, FeatureDef> = {
  [FEATURE_KEYS.UNLIMITED_ALBUMS]: {
    label: 'Unlimited albums',
    pitch: 'Create as many albums as you need — no cap.',
    requiredTier: 'pro',
    upgradeTo: 'Gallery',
  },
  [FEATURE_KEYS.GALLERY_3D]: {
    label: '3D Walkable Gallery',
    pitch: 'Turn any album into a full 3D gallery your guests can walk through.',
    requiredTier: 'pro',
    upgradeTo: 'Gallery',
  },
  [FEATURE_KEYS.MULTI_SOURCE_ALBUMS]: {
    label: 'Multi-album Studio',
    pitch: 'Pull prints from more than one album into the same 3D gallery.',
    requiredTier: 'pro',
    upgradeTo: 'Gallery',
  },
  [FEATURE_KEYS.STUDIO_CUSTOM_FINISHES]: {
    label: 'Custom finishes',
    pitch: 'Pick any wall and floor color — beyond the curated Studio palette.',
    requiredTier: 'pro',
    upgradeTo: 'Gallery',
  },
  [FEATURE_KEYS.GUEST_UPLOADS]: {
    label: 'Guest uploads',
    pitch: 'Let guests add their own photos to your shared album.',
    requiredTier: 'pro',
    upgradeTo: 'Gallery',
  },
  [FEATURE_KEYS.VOICE_NOTES]: {
    label: 'Voice notes',
    pitch: 'Attach voice recordings to photos for richer stories.',
    requiredTier: 'pro',
    upgradeTo: 'Gallery',
  },
  [FEATURE_KEYS.ANNIVERSARY_REOPENING]: {
    label: 'Anniversary reopenings',
    pitch: 'Automatically re-open albums on anniversaries to relive moments.',
    requiredTier: 'pro',
    upgradeTo: 'Gallery',
  },
  [FEATURE_KEYS.STUDIO_MULTI_ROOM]: {
    label: 'Multi-room galleries',
    pitch: 'Add foyer, hallway, and annex rooms — guests walk the whole suite on one key.',
    requiredTier: 'enterprise',
    upgradeTo: 'Studio',
  },
  [FEATURE_KEYS.STUDIO_PREMIUM_SCENES]: {
    label: 'Loft & Pavilion scenes',
    pitch: 'Unlock the Loft and Pavilion room shapes beyond the free Corridor.',
    requiredTier: 'pro',
    upgradeTo: 'Gallery',
  },
  [FEATURE_KEYS.CLIENT_WORKSPACES]: {
    label: 'Client workspaces',
    pitch: 'Deliver branded, private workspaces to your photography clients.',
    requiredTier: 'enterprise',
    upgradeTo: 'Studio',
  },
  [FEATURE_KEYS.RAW_PROOFING]: {
    label: 'RAW proofing',
    pitch: 'Deliver and proof RAW files directly in Egowall.',
    requiredTier: 'enterprise',
    upgradeTo: 'Studio',
  },
  [FEATURE_KEYS.ANALYTICS]: {
    label: 'Album analytics',
    pitch: 'Track guest views, engagement, and download activity.',
    requiredTier: 'enterprise',
    upgradeTo: 'Studio',
  },
  [FEATURE_KEYS.STUDIO_PRINT_QUOTA]: {
    label: 'More prints per room',
    pitch: 'Hang more photos on the same wall before the room is full.',
    requiredTier: 'pro',
    upgradeTo: 'Gallery',
  },
  [FEATURE_KEYS.FULL_RESOLUTION_UPLOAD]: {
    label: 'Full-resolution uploads',
    pitch: 'Upload at up to 4000px instead of the standard 1000px — sharper prints and Studio walls.',
    requiredTier: 'pro',
    upgradeTo: 'Gallery',
  },
}

/**
 * Placeholder upload resize ceilings, in px (long edge) — mirrors the
 * placeholder-pending-sign-off pattern already used by MAX_PRINTS_PER_ROOM.
 * Free stays at today's existing behavior; Pro/Studio get a real bump. See
 * issues/docs/heic-and-full-resolution-upload-spec.md §B3.
 */
export const UPLOAD_RESIZE_DIM = {
  standard: 1000,
  full: 4000,
} as const

/**
 * Placeholder per-tier prints-per-room caps — real numbers not yet locked
 * with product/pricing. See issues/docs/studio-print-limits-plan-gate-todo.md.
 * The hang path and Layers panel counter already enforce whatever's here,
 * so tightening/loosening this later is a one-line change.
 */
export const MAX_PRINTS_PER_ROOM: Record<'free' | 'pro' | 'enterprise', number> = {
  free: 8,
  pro: 24,
  enterprise: Number.POSITIVE_INFINITY,
}

export function maxPrintsForTier(tier: string): number {
  return MAX_PRINTS_PER_ROOM[tier as keyof typeof MAX_PRINTS_PER_ROOM] ?? MAX_PRINTS_PER_ROOM.free
}

/** Maximum albums a free (Keepsake) user can create before seeing the upgrade banner. */
export const FREE_ALBUM_LIMIT = 3

/**
 * Returns true if the given tier satisfies the feature's minimum requirement.
 * tier: 'free' | 'pro' | 'enterprise'
 */
export function isFeatureAvailableForTier(tier: string, featureKey: FeatureKey): boolean {
  const def = FEATURES_MAP[featureKey]
  if (!def) return true
  if (def.requiredTier === 'pro') return tier === 'pro' || tier === 'enterprise'
  if (def.requiredTier === 'enterprise') return tier === 'enterprise'
  return false
}
